import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Chip,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import { toast } from 'react-toastify';

import api from '../../services/api';
import { PageLayout, PageHeader, SectionCard, EmptyState } from '../../components/ui';

const ACCEPT = '.pdf,.docx,.txt';
const MAX_MB = 12;

export default function PoliciesPage() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState('file'); // 'file' | 'text'
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const fileInputRef = useRef(null);

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/bot/policies');
      setDocuments(Array.isArray(data?.documents) ? data.documents : []);
    } catch (e) {
      toast.error('Could not load policy documents.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, []);

  const resetForm = () => {
    setTitle('');
    setContent('');
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFilePick = (e) => {
    const picked = e.target.files?.[0] || null;
    if (picked && picked.size > MAX_MB * 1024 * 1024) {
      toast.error(`File is too large (max ${MAX_MB} MB).`);
      e.target.value = '';
      return;
    }
    setFile(picked);
    // Default the title to the file name (without extension) if empty.
    if (picked && !title.trim()) {
      setTitle(picked.name.replace(/\.[^.]+$/, ''));
    }
  };

  const canSubmit =
    title.trim() && !submitting && (mode === 'file' ? !!file : !!content.trim());

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      let payload;
      if (mode === 'file') {
        payload = new FormData();
        payload.append('title', title.trim());
        payload.append('file', file);
      } else {
        payload = { title: title.trim(), content };
      }
      const { data } = await api.post('/bot/policies', payload);
      toast.success(data?.reply || 'Policy added.');
      resetForm();
      fetchDocuments();
    } catch (e) {
      toast.error(e?.response?.data?.reply || 'Could not add the policy document.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm('Remove this policy document from the assistant?')) return;
    setDeleting(doc);
    try {
      await api.delete(`/bot/policies/${encodeURIComponent(doc)}`);
      toast.success('Policy document removed.');
      setDocuments((prev) => prev.filter((d) => d.doc !== doc));
    } catch (e) {
      toast.error(e?.response?.data?.reply || 'Could not remove the document.');
    } finally {
      setDeleting(null);
    }
  };

  return (
    <PageLayout maxWidth="md" backgroundAccent>
      <PageHeader
        overline="HR Assistant"
        title="Policy Knowledge Base"
        subtitle="Upload your employee handbook and policy documents (PDF, DOCX or TXT). The HR Assistant extracts the text locally and uses it to answer staff questions about company policies — nothing leaves the server."
      />

      <Stack spacing={2.5}>
        <SectionCard title="Add a policy document">
          <Stack spacing={2}>
            <ToggleButtonGroup
              exclusive
              size="small"
              color="primary"
              value={mode}
              onChange={(_, v) => v && setMode(v)}
            >
              <ToggleButton value="file">
                <UploadFileOutlinedIcon fontSize="small" sx={{ mr: 0.75 }} /> Upload file
              </ToggleButton>
              <ToggleButton value="text">
                <DescriptionOutlinedIcon fontSize="small" sx={{ mr: 0.75 }} /> Paste text
              </ToggleButton>
            </ToggleButtonGroup>

            <TextField
              label="Title"
              placeholder="e.g. Employee Handbook 2026"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              fullWidth
              required
              inputProps={{ maxLength: 160 }}
            />

            {mode === 'file' ? (
              <Box>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ACCEPT}
                  hidden
                  onChange={handleFilePick}
                />
                <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Button
                    variant="outlined"
                    startIcon={<UploadFileOutlinedIcon />}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Choose file
                  </Button>
                  {file ? (
                    <Chip
                      label={file.name}
                      onDelete={() => {
                        setFile(null);
                        if (fileInputRef.current) fileInputRef.current.value = '';
                      }}
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      PDF, DOCX or TXT · up to {MAX_MB} MB
                    </Typography>
                  )}
                </Stack>
              </Box>
            ) : (
              <TextField
                label="Policy text"
                placeholder="Paste the policy or handbook text here…"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                fullWidth
                multiline
                minRows={6}
                maxRows={16}
                inputProps={{ maxLength: 200000 }}
              />
            )}

            <Box>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={!canSubmit}
                startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
              >
                {submitting ? 'Adding…' : 'Add to assistant'}
              </Button>
            </Box>
          </Stack>
        </SectionCard>

        <SectionCard
          title="Indexed documents"
          subheader={documents.length ? `${documents.length} document${documents.length === 1 ? '' : 's'}` : undefined}
        >
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : documents.length === 0 ? (
            <EmptyState
              compact
              icon={MenuBookOutlinedIcon}
              title="No policy documents yet"
              description="Add your first handbook or policy above so the assistant can answer policy questions."
            />
          ) : (
            <List disablePadding>
              {documents.map((d) => (
                <ListItem
                  key={d.doc}
                  divider
                  secondaryAction={
                    <Tooltip title="Remove">
                      <span>
                        <IconButton
                          edge="end"
                          color="error"
                          disabled={deleting === d.doc}
                          onClick={() => handleDelete(d.doc)}
                        >
                          {deleting === d.doc ? <CircularProgress size={18} /> : <DeleteOutlineIcon />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  }
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <MenuBookOutlinedIcon color="action" />
                  </ListItemIcon>
                  <ListItemText primary={d.title} secondary={d.doc} />
                </ListItem>
              ))}
            </List>
          )}
        </SectionCard>
      </Stack>
    </PageLayout>
  );
}
